import { AVATAR_CATALOG, type AvatarConfig, type AvatarField } from './avatar-catalog'

export const AVATAR_CATEGORY_LABELS: Record<AvatarField, string> = {
  face: 'Rosto',
  body: 'Corpo',
  hair: 'Cabelos',
  eyes: 'Olhos',
  brows: 'Sobrancelhas',
  mouth: 'Boca',
  facialHair: 'Barba e bigode',
  clothing: 'Roupas',
  accessory: 'Óculos',
  earrings: 'Brincos',
  headwear: 'Chapéus e acessórios',
  detail: 'Detalhes do rosto',
  skin: 'Tom de pele',
  hairColor: 'Cor do cabelo',
  eyeColor: 'Cor dos olhos',
  clothingColor: 'Cor da roupa',
  accessoryColor: 'Cor dos óculos',
  headwearColor: 'Cor do chapéu',
  background: 'Fundos',
}

export type AvatarLayer = {
  kind: 'image' | 'text' | 'shape'
  x: number
  y: number
  width: number
  height: number
  rotation: number
  opacity: number
  color: string
  text?: string | null
  shape?: 'circle' | 'rectangle' | 'star' | 'heart' | 'diamond' | null
  asset_id?: number | null
  url?: string
}
export type AvatarDesign = {
  base: string | null
  color: string | null
  layers: AvatarLayer[]
}
export type AvatarPiece = {
  id: number
  campaign_id: number | null
  category: AvatarField
  name: string
  design: AvatarDesign
  price_cents?: number
  owned?: boolean
}
export type CampaignAvatarItem = AvatarPiece & {
  status: 'draft' | 'published' | 'archived'
  sort_order: number
  revision: number
}

const hex = (value: unknown): value is string =>
  typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value)
const numberIn = (value: unknown, min: number, max: number): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max
const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

export function safeAvatarImageUrl(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > 2048) return false
  if (/^\/(?!\/)[^\\\s]+$/.test(value)) return true
  try {
    const url = new URL(value)
    return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password
  } catch {
    return false
  }
}

/** Validate persisted snapshots before they reach SVG attributes. No markup or CSS. */
export function parseAvatarPiece(value: unknown): AvatarPiece | null {
  if (
    !record(value) ||
    !Number.isSafeInteger(value.id) ||
    Number(value.id) < 1 ||
    (value.campaign_id !== null &&
      (!Number.isSafeInteger(value.campaign_id) || Number(value.campaign_id) < 1)) ||
    typeof value.category !== 'string' ||
    !Object.prototype.hasOwnProperty.call(AVATAR_CATALOG, value.category) ||
    typeof value.name !== 'string' ||
    [...value.name].length > 80 ||
    !record(value.design)
  )
    return null
  const category = value.category as AvatarField
  if (
    value.price_cents !== undefined &&
    (!numberIn(value.price_cents, 0, 99999999) || !Number.isInteger(value.price_cents))
  )
    return null
  const d = value.design
  if (d.base !== null && !AVATAR_CATALOG[category].some(([id]) => id === d.base))
    return null
  if (d.color !== null && !hex(d.color)) return null
  if (!Array.isArray(d.layers) || d.layers.length > 8) return null
  const layers: AvatarLayer[] = []
  for (const l of d.layers) {
    if (
      !record(l) ||
      !['image', 'text', 'shape'].includes(String(l.kind)) ||
      !numberIn(l.x, -240, 240) ||
      !numberIn(l.y, -280, 280) ||
      !numberIn(l.width, 1, 480) ||
      !numberIn(l.height, 1, 560) ||
      !numberIn(l.rotation, -180, 180) ||
      !numberIn(l.opacity, 0.05, 1) ||
      !hex(l.color)
    )
      return null
    const layer: AvatarLayer = {
      kind: l.kind as AvatarLayer['kind'],
      x: l.x,
      y: l.y,
      width: l.width,
      height: l.height,
      rotation: l.rotation,
      opacity: l.opacity,
      color: l.color,
    }
    if (l.kind === 'image') {
      if (
        !safeAvatarImageUrl(l.url) ||
        !Number.isSafeInteger(l.asset_id) ||
        Number(l.asset_id) < 1
      )
        return null
      layer.url = l.url
      layer.asset_id = Number(l.asset_id)
    } else if (l.kind === 'text') {
      if (
        typeof l.text !== 'string' ||
        [...l.text].length > 32 ||
        /[<>]/.test(l.text) ||
        [...l.text].some((char) => char.charCodeAt(0) < 32)
      )
        return null
      layer.text = l.text
    } else {
      if (!['circle', 'rectangle', 'star', 'heart', 'diamond'].includes(String(l.shape)))
        return null
      layer.shape = l.shape as AvatarLayer['shape']
    }
    layers.push(layer)
  }
  return {
    id: Number(value.id),
    campaign_id: value.campaign_id === null ? null : Number(value.campaign_id),
    category,
    name: value.name,
    ...(value.price_cents !== undefined
      ? { price_cents: value.price_cents as number }
      : {}),
    ...(value.owned === true ? { owned: true } : {}),
    design: { base: d.base as string | null, color: d.color as string | null, layers },
  }
}

export function equipAvatarPiece(config: AvatarConfig, piece: AvatarPiece): AvatarConfig {
  return {
    ...config,
    custom: [
      ...(config.custom ?? []).filter((p) => p.category !== piece.category),
      piece,
    ],
  }
}

export function removeAvatarPiece(
  config: AvatarConfig,
  category: AvatarField
): AvatarConfig {
  const custom = config.custom?.filter((p) => p.category !== category)
  const next = { ...config }
  if (custom?.length) next.custom = custom
  else delete next.custom
  return next
}

export const AVATAR_COLOR_FIELD: Partial<Record<AvatarField, AvatarField>> = {
  face: 'skin',
  body: 'skin',
  hair: 'hairColor',
  eyes: 'eyeColor',
  clothing: 'clothingColor',
  accessory: 'accessoryColor',
  headwear: 'headwearColor',
}

export function avatarWithCustomBases(config: AvatarConfig): AvatarConfig {
  const values: Record<string, unknown> = { ...config }
  // Structural pieces first; explicit color pieces take precedence.
  for (const piece of config.custom ?? []) {
    if (piece.design.base !== null) values[piece.category] = piece.design.base
    else if (
      ['facialHair', 'accessory', 'earrings', 'headwear', 'detail'].includes(
        piece.category
      )
    )
      values[piece.category] = 'none'
    if (piece.design.color && AVATAR_COLOR_FIELD[piece.category]) {
      values[AVATAR_COLOR_FIELD[piece.category]!] = piece.design.color
    }
  }
  for (const piece of config.custom ?? []) {
    if (
      !AVATAR_COLOR_FIELD[piece.category] &&
      piece.design.color &&
      [
        'skin',
        'hairColor',
        'eyeColor',
        'clothingColor',
        'accessoryColor',
        'headwearColor',
        'background',
      ].includes(piece.category)
    ) {
      values[piece.category] = piece.design.color
    }
  }
  return values as AvatarConfig
}

export function hidesAvatarBase(config: AvatarConfig, category: AvatarField): boolean {
  return !!config.custom?.some((p) => p.category === category && p.design.base === null)
}

export function avatarPieceColor(
  config: AvatarConfig,
  category: AvatarField,
  fallback: string
): string {
  return (
    config.custom?.find((piece) => piece.category === category)?.design.color ?? fallback
  )
}
